import { useEffect, useRef, useState } from 'react'
import Toggle from './Toggle'
import { SECURITY_RIGHTS } from '../pages/adminMockData'
import styles from './InviteUserModal.module.css'

/**
 * InviteUserModal — "Add a new user" dialog: full name + email, plus a
 * security-rights toggle per row. Mirrors AddSkillModal's non-dismissable
 * shell (Cancel-only close) so an in-progress invite isn't lost to a stray
 * click.
 *
 * Props:
 *   existingEmails {string[]} — every current user's email (any status,
 *     including your own hidden-from-the-list one); checked case-insensitively
 *     against the entered address so an admin can't invite a duplicate.
 *     gaussiq-identity has no unique constraint on users.email, so this is a
 *     UI-only guard, not a backend-enforced one.
 *   onInvite {function} — called with { fullName, email, rights }
 *   onCancel {function}
 */
export default function InviteUserModal({ existingEmails = [], onInvite, onCancel }) {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [rights, setRights] = useState({ admin: false, orgSkillManagement: false, teamSkillManagement: false })
  const firstFieldRef = useRef(null)

  useEffect(() => {
    firstFieldRef.current?.focus()
  }, [])

  const trimmedEmail = email.trim()
  const isValidFormat = /\S+@\S+\.\S+/.test(trimmedEmail)
  const isDuplicate = isValidFormat
    && existingEmails.some(e => e.toLowerCase() === trimmedEmail.toLowerCase())
  const canSend = fullName.trim() !== '' && isValidFormat && !isDuplicate

  function handleSend() {
    if (!canSend) return
    onInvite({ fullName: fullName.trim(), email: email.trim(), rights })
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Add a new user">
        <span className={styles.eyebrow}>Invite by email</span>
        <h2 className={styles.title}>Add a new user</h2>
        <p className={styles.message}>They receive an invitation and pick their own password.</p>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Full Name</span>
          <input
            ref={firstFieldRef}
            className={styles.input}
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="Jane Doe"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Email Address</span>
          <input
            className={styles.input}
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="name@company.com"
            aria-invalid={isDuplicate}
          />
          {isDuplicate && (
            <span className={styles.fieldError}>A user with this email already exists.</span>
          )}
        </label>

        <div className={styles.rightsField}>
          <span className={styles.fieldLabel}>Security Rights</span>
          <div className={styles.rightsList}>
            {SECURITY_RIGHTS.map(r => (
              <div key={r.key} className={styles.rightCard}>
                <Toggle
                  label={r.label}
                  description={r.description}
                  tone={r.tone}
                  checked={rights[r.key]}
                  onChange={value => setRights(prev => ({ ...prev, [r.key]: value }))}
                />
              </div>
            ))}
          </div>
        </div>

        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
          <button className={styles.sendBtn} onClick={handleSend} disabled={!canSend}>
            Send invitation
          </button>
        </div>
      </div>
    </div>
  )
}
